/**
 * Numune Takip — FAZ 1 + FAZ 2
 *
 * Faz 2 eklentileri:
 *  - Çoklu ürün/varyant satırı (items[])
 *  - Kaynak depo seçimi (Stok veya Stok Dışı Numune)
 *  - Kısmi iade + hedef depo
 *  - Bedelsiz/sarf kapatma
 *  - Satışa Dönüştür (Sipariş Sihirbazına prefill — çift stok hareketi YOK)
 *
 * Tüm veri modelleri backend `_normalize_read()` ile hem eski hem yeni
 * kayıtları `items[] + kpi{}` ile döner.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import SearchPicker from "@/components/SearchPicker";
import PendingSamplePhotos from "@/components/PendingSamplePhotos";
import {
  Package, Users, Plus, Edit3, ShoppingCart, AlertTriangle, Loader2, Boxes,
  Clock, X, Trash2, PackageCheck, PackageX, ArrowLeftRight, Gift, ExternalLink,
  Camera, Image as ImageIcon, ScanLine,
} from "lucide-react";
import BarcodeScanner from "@/components/BarcodeScanner";
import { lookupBarcode, UnknownBarcodeDialog } from "@/components/barcode/BarcodeFlow";

const STATUS_LABEL = {
  hazirlaniyor: "Hazırlanıyor",
  musteride: "Müşteride",
  kismi_iade: "Kısmi İade",
  tam_iade: "Tam İade",
  geri_alindi: "Geri Alındı",
  satisa_dondu: "Satışa Döndü",
  kayip: "Kayıp",
  tamamlandi: "Tamamlandı",
};
const STATUS_TONE = {
  hazirlaniyor: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  musteride: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  kismi_iade: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  tam_iade: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  geri_alindi: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  satisa_dondu: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
  kayip: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  tamamlandi: "bg-emerald-600/20 text-emerald-200 border-emerald-500/50",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("tr-TR"); } catch { return iso.slice(0, 10); }
}

function normalizeItemLabel(it) {
  const parts = [it.product_name || it.brand_name || "—"];
  if (it.variant_name) parts.push(it.variant_name);
  return parts.join(" · ");
}

export default function Samples() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const canAdd = can("samples", "add");
  const canEdit = can("samples", "edit");

  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({ musteride: 0, gecikmis: 0, geri_alindi: 0, satisa_dondu: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [createOpen, setCreateOpen] = useState(false);
  const [detailSample, setDetailSample] = useState(null);
  const [returnDialog, setReturnDialog] = useState(null);
  const [compDialog, setCompDialog] = useState(null);
  const [convertDialog, setConvertDialog] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [productCache, setProductCache] = useState({}); // id → ürün (seçim yeni aramada kaybolmasın)
  // Server-side ürün arama — mevcut canonical GET /products?search= (SearchPicker debounce'u ile)
  const searchProducts = useCallback(async (q) => {
    const { data } = await api.get("/products", { params: { search: q, limit: 50 } });
    const arr = Array.isArray(data) ? data : [];
    setProductCache((c) => { const n = { ...c }; arr.forEach((p) => { if (p?.id) n[p.id] = p; }); return n; });
    return arr;
  }, []);
  const [warehouses, setWarehouses] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== "all") params.status = statusFilter;
      const [{ data }, { data: k }] = await Promise.all([
        api.get("/samples", { params }),
        api.get("/samples/kpis"),
      ]);
      setRows(Array.isArray(data) ? data : []);
      setKpis(k || {});
    } catch (e) { toast.error(formatApiError(e, "Numuneler yüklenemedi")); }
    finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [c, u, w] = await Promise.all([
          api.get("/customers"),
          api.get("/users/directory"),
          api.get("/warehouses"),
        ]);
        setCustomers(c.data || []);
        setUsers(u.data || []);
        setWarehouses(Array.isArray(w.data) ? w.data : (w.data?.warehouses || []));
      } catch (e) { toast.error(formatApiError(e, "Referans veriler yüklenemedi")); }
    })();
  }, []);

  const nowIso = new Date().toISOString();
  const isOverdue = (r) => (r.status === "musteride" || r.status === "kismi_iade")
    && r.planned_return_at && r.planned_return_at < nowIso;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Numune Takip"
        subtitle="Çoklu ürün / varyant · Kaynak depo · Kısmi iade · Satışa dönüştürme"
        icon={<Boxes className="w-6 h-6" />}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => api.post("/samples/scan-overdue").then((r) => {
                toast.success(`Tarandı: ${r.data.scanned} · Bildirim: ${r.data.sent}`);
              }).catch((e) => toast.error(formatApiError(e)))}
              disabled={!canEdit}
              data-testid="samples-scan-overdue-btn"
              className="rounded-sm border-slate-700 text-xs"
            >
              <Clock className="w-3.5 h-3.5 mr-1.5" /> Gecikme Tara
            </Button>
            <Button
              onClick={() => setCreateOpen(true)}
              disabled={!canAdd}
              title={canAdd ? "" : "Numune oluşturma yetkiniz yok"}
              className="bg-cyan-600 hover:bg-cyan-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
              data-testid="samples-add-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Numune Bırak
            </Button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Müşteride" value={kpis.musteride} icon={Users} tone="sky" testId="kpi-musteride" />
        <KpiCard label="Gecikmiş" value={kpis.gecikmis} icon={AlertTriangle} tone="rose" testId="kpi-gecikmis" />
        <KpiCard label="Geri Alındı" value={kpis.geri_alindi} icon={PackageCheck} tone="emerald" testId="kpi-geri-alindi" />
        <KpiCard label="Satışa Döndü" value={kpis.satisa_dondu} icon={ShoppingCart} tone="cyan" testId="kpi-satisa-dondu" />
      </div>

      {/* Filter bar */}
      <Card className="bg-slate-900 border-slate-800 rounded-sm p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Müşteri / Ürün / Personel ara..."
              className="rounded-sm bg-slate-950 border-slate-800"
              data-testid="samples-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] rounded-sm bg-slate-950 border-slate-800" data-testid="samples-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm durumlar</SelectItem>
              {Object.entries(STATUS_LABEL).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Rows table */}
      <Card className="bg-slate-900 border-slate-800 rounded-sm">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800">
              <TableHead className="text-slate-400 text-[10px] uppercase tracking-wider">Müşteri / Ürün</TableHead>
              <TableHead className="text-slate-400 text-[10px] uppercase tracking-wider text-center">Verilen</TableHead>
              <TableHead className="text-slate-400 text-[10px] uppercase tracking-wider text-center">İade</TableHead>
              <TableHead className="text-slate-400 text-[10px] uppercase tracking-wider text-center">Müşteride</TableHead>
              <TableHead className="text-slate-400 text-[10px] uppercase tracking-wider">Bırakan / Tarih</TableHead>
              <TableHead className="text-slate-400 text-[10px] uppercase tracking-wider">Durum</TableHead>
              <TableHead className="text-slate-400 text-[10px] uppercase tracking-wider text-right">Aksiyon</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Yükleniyor…</TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">Kayıt bulunamadı</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} className="border-slate-800 hover:bg-slate-800/30" data-testid={`sample-row-${r.id}`}>
                <TableCell>
                  <div className="font-semibold text-slate-100">{r.customer_name || "—"}</div>
                  <div className="text-[11px] text-slate-500 truncate max-w-[300px]">
                    {(r.items || []).slice(0, 2).map((it, i) => (
                      <span key={i}>{i > 0 && " · "}{normalizeItemLabel(it)} ×{it.given_qty}</span>
                    ))}
                    {(r.items || []).length > 2 && <span> +{r.items.length - 2} daha</span>}
                  </div>
                </TableCell>
                <TableCell className="text-center font-mono text-slate-200">{r.kpi?.given ?? r.quantity ?? 0}</TableCell>
                <TableCell className="text-center font-mono text-emerald-400">{r.kpi?.returned ?? 0}</TableCell>
                <TableCell className="text-center font-mono">
                  <span className={(r.kpi?.at_customer ?? 0) > 0 ? "text-sky-400 font-bold" : "text-slate-500"}>
                    {r.kpi?.at_customer ?? 0}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="text-slate-300 text-xs">{r.dropper_user_name || "—"}</div>
                  <div className="text-slate-500 text-[10px]">{fmtDate(r.dropped_at)}</div>
                </TableCell>
                <TableCell>
                  <Badge className={`text-[10px] rounded-sm px-2 py-0.5 ${STATUS_TONE[r.status] || "bg-slate-800 text-slate-300"}`}>
                    {STATUS_LABEL[r.status] || r.status}
                  </Badge>
                  {isOverdue(r) && (
                    <Badge className="ml-1 bg-rose-500/20 text-rose-300 border-rose-500/40 text-[10px] rounded-sm px-1.5 py-0.5">
                      <AlertTriangle className="w-3 h-3 mr-0.5 inline" /> Gecikmiş
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => setDetailSample(r)} className="h-7 rounded-sm text-xs" data-testid={`sample-detail-${r.id}`}>
                    Detay
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create dialog */}
      {createOpen && (
        <CreateSampleDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSaved={() => { setCreateOpen(false); load(); }}
          customers={customers}
          users={users}
          searchProducts={searchProducts}
          productCache={productCache}
          warehouses={warehouses}
        />
      )}

      {/* Detail dialog */}
      {detailSample && (
        <DetailDialog
          sample={detailSample}
          onClose={() => setDetailSample(null)}
          onReturn={() => { setReturnDialog(detailSample); setDetailSample(null); }}
          onComplimentary={() => { setCompDialog(detailSample); setDetailSample(null); }}
          canEdit={canEdit}
          onNavigateSale={() => {
            setConvertDialog(detailSample);
            setDetailSample(null);
          }}
        />
      )}

      {/* Return dialog */}
      {returnDialog && (
        <ReturnDialog
          sample={returnDialog}
          warehouses={warehouses}
          searchProducts={searchProducts}
          productCache={productCache}
          onClose={() => setReturnDialog(null)}
          onSaved={() => { setReturnDialog(null); load(); }}
        />
      )}

      {/* Complimentary dialog */}
      {compDialog && (
        <ComplimentaryDialog
          sample={compDialog}
          onClose={() => setCompDialog(null)}
          onSaved={() => { setCompDialog(null); load(); }}
        />
      )}

      {/* Convert-to-sale dialog: picks items+qty, writes to sessionStorage, navigates to OrderWizard */}
      {convertDialog && (
        <ConvertToSaleDialog
          sample={convertDialog}
          onClose={() => setConvertDialog(null)}
          onConfirm={(intent) => {
            // Store intent for OrderWizard to consume on mount.
            // Kullanıcı sipariş oluşturursa OrderWizard mark-converted çağırır.
            // Başarısızsa/iptal ederse sessionStorage'ı silmese bile idempotency korur.
            try {
              sessionStorage.setItem("operix.sample_convert", JSON.stringify(intent));
            } catch { /* ignore */ }
            navigate(`/siparis-sihirbazi?customer=${convertDialog.customer_id}&from_sample=${convertDialog.id}`);
            setConvertDialog(null);
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// KPI Card
// ============================================================================
function KpiCard({ label, value, icon: Icon, tone, testId }) {
  const toneMap = {
    sky: "bg-sky-600 text-sky-300",
    rose: "bg-rose-600 text-rose-300",
    emerald: "bg-emerald-600 text-emerald-300",
    cyan: "bg-cyan-600 text-cyan-300",
  };
  const [bg, txt] = (toneMap[tone] || "bg-slate-700 text-slate-300").split(" ");
  return (
    <Card className="bg-slate-900 border-slate-800 rounded-sm p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className={`w-8 h-8 ${bg} flex items-center justify-center rounded-sm`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
      <div className="text-2xl font-bold font-mono text-slate-50">{value}</div>
      <div className={`text-[10px] uppercase tracking-wider ${txt} mt-0.5`}>{label}</div>
    </Card>
  );
}

// ============================================================================
// Create Dialog (multi-item)
// ============================================================================
function CreateSampleDialog({ open, onClose, onSaved, customers, users, searchProducts, productCache, warehouses }) {
  const today = new Date().toISOString().slice(0, 10);
  const [customerId, setCustomerId] = useState("");
  const [dropperId, setDropperId] = useState("");
  const [droppedAt, setDroppedAt] = useState(today);
  const [receiverName, setReceiverName] = useState("");
  const [plannedReturn, setPlannedReturn] = useState("");
  const [notes, setNotes] = useState("");
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [items, setItems] = useState([emptyItem()]);
  const [saving, setSaving] = useState(false);

  function emptyItem() {
    return {
      _lid: Math.random().toString(36).slice(2),
      product_id: "", variant_id: "", product_name: "", product_code: "",
      variant_name: "", brand_name: "",
      source_type: "stock", source_warehouse_id: "",
      given_qty: 1, note: "",
    };
  }
  const addRow = () => setItems((s) => [...s, emptyItem()]);
  const removeRow = (lid) => setItems((s) => s.length > 1 ? s.filter((i) => i._lid !== lid) : s);
  const patchRow = (lid, p) => setItems((s) => s.map((i) => i._lid === lid ? { ...i, ...p } : i));
  // Barkod: tam eşleşme → ürün + gerçek beden (variant_id); variant_name backend'de varyanttan türetilir
  const [scanLid, setScanLid] = useState("");
  const [unknownScan, setUnknownScan] = useState(null); // {code, lid}
  const [scanned, setScanned] = useState({});
  const applyScan = (lid, r) => {
    setScanned((c) => ({ ...c, [r.product.id]: { id: r.product.id, name: r.product.name, code: r.product.product_code || "", brand_name: r.product.brand || "" } }));
    patchRow(lid, { product_id: r.product.id, variant_id: r.variant?.id || "", variant_name: r.size || r.variant?.sku || "" });
    toast.success(`${r.product.name}${r.size ? ` · ${r.size}` : ""} seçildi`);
  };
  const onSampleScan = async (code) => {
    const lid = scanLid;
    try {
      const r = await lookupBarcode(code);
      if (r.found) applyScan(lid, r); else setUnknownScan({ code: r.code, lid });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async () => {
    if (!customerId || !dropperId || !droppedAt) { toast.error("Müşteri / Personel / Tarih zorunlu"); return; }
    const cleaned = items.map((it) => {
      const base = {
        source_type: it.source_type,
        given_qty: Number(it.given_qty) || 1,
        note: it.note || "",
      };
      if (it.source_type === "stock") {
        return { ...base, product_id: it.product_id, variant_id: it.variant_id || "", source_warehouse_id: it.source_warehouse_id };
      }
      return { ...base, product_name: it.product_name, product_code: it.product_code, variant_name: it.variant_name, brand_name: it.brand_name };
    });
    // Validate
    for (const it of cleaned) {
      if (it.source_type === "stock" && (!it.product_id || !it.source_warehouse_id)) {
        toast.error("Stoklu satırda ürün + kaynak depo zorunlu"); return;
      }
      if (it.source_type === "off_stock" && !it.product_name) {
        toast.error("Stok Dışı satırda ürün adı zorunlu"); return;
      }
    }
    setSaving(true);
    try {
      const { data: created } = await api.post("/samples", {
        customer_id: customerId,
        dropper_user_id: dropperId,
        dropped_at: droppedAt,
        receiver_name: receiverName || "",
        planned_return_at: plannedReturn || null,
        notes: notes || "",
        items: cleaned,
      });
      // Numune fotoğrafları — kayıt oluştuktan sonra mevcut photos endpoint'ine sırayla
      if (created?.id && pendingPhotos.length > 0) {
        let ok = 0;
        for (const p of pendingPhotos) {
          try {
            const fd = new FormData();
            fd.append("file", p.file);
            await api.post(`/samples/${created.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            ok++;
          } catch (e) { toast.error(`${p.file.name}: ${formatApiError(e)}`); }
        }
        if (ok > 0) toast.success(`${ok} fotoğraf yüklendi`);
        pendingPhotos.forEach((p) => URL.revokeObjectURL(p.url));
        setPendingPhotos([]);
      }
      toast.success("Numune oluşturuldu");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl bg-slate-900 border-slate-800" data-testid="sample-create-dialog">
        <DialogHeader>
          <DialogTitle>Yeni Numune Bırak</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Müşteri *</Label>
              <SearchPicker
                items={customers}
                value={customerId}
                onChange={setCustomerId}
                placeholder="Müşteri seç..."
                testId="sample-customer"
                getLabel={(c) => c.name}
                getSubtitle={(c) => c.tax_no || c.phone || ""}
              />
            </div>
            <div>
              <Label className="text-xs">Bırakan Personel *</Label>
              <SearchPicker
                items={users}
                value={dropperId}
                onChange={setDropperId}
                placeholder="Personel seç..."
                testId="sample-dropper"
                getLabel={(u) => u.name || u.email}
                getSubtitle={(u) => u.email || ""}
              />
            </div>
            <div>
              <Label className="text-xs">Bırakma Tarihi *</Label>
              <Input type="date" value={droppedAt} onChange={(e) => setDroppedAt(e.target.value)} className="rounded-sm bg-slate-950 border-slate-800" data-testid="sample-dropped-at" />
            </div>
            <div>
              <Label className="text-xs">Teslim Alan (İsim)</Label>
              <Input value={receiverName} onChange={(e) => setReceiverName(e.target.value)} className="rounded-sm bg-slate-950 border-slate-800" placeholder="Müşteride teslim alan kişi" />
            </div>
            <div>
              <Label className="text-xs">Planlanan Geri Alma</Label>
              <Input type="date" value={plannedReturn} onChange={(e) => setPlannedReturn(e.target.value)} className="rounded-sm bg-slate-950 border-slate-800" />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs">Not</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm bg-slate-950 border-slate-800" placeholder="Genel not..." />
            </div>
          </div>

          {/* Numune Fotoğrafları — Not ile Ürünler arasında; kayıt sonrası mevcut photos endpoint'ine yüklenir */}
          <div className="border-t border-slate-800 pt-3">
            <PendingSamplePhotos files={pendingPhotos} onChange={setPendingPhotos} disabled={saving} />
          </div>

          <div className="border-t border-slate-800 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-200">Ürünler ({items.length})</div>
              <Button type="button" onClick={addRow} variant="outline" size="sm" className="rounded-sm text-xs border-slate-700" data-testid="sample-add-item">
                <Plus className="w-3 h-3 mr-1" /> Satır Ekle
              </Button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={it._lid} className="border border-slate-800 rounded-sm p-3 space-y-2" data-testid={`sample-item-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] text-slate-500 uppercase tracking-wider">Satır #{idx + 1}</div>
                    {items.length > 1 && (
                      <Button type="button" onClick={() => removeRow(it._lid)} variant="ghost" size="sm" className="h-6 w-6 p-0 text-rose-400 hover:text-rose-300">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase text-slate-500">Kaynak</Label>
                      <Select value={it.source_type} onValueChange={(v) => patchRow(it._lid, { source_type: v, product_id: "", variant_id: "", source_warehouse_id: "" })}>
                        <SelectTrigger className="h-8 rounded-sm bg-slate-950 border-slate-800 text-xs" data-testid={`sample-item-source-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="stock">Depodan (Stoklu)</SelectItem>
                          <SelectItem value="off_stock">Stok Dışı Numune</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {it.source_type === "stock" ? (
                      <>
                        <div className="md:col-span-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-[10px] uppercase text-slate-500">Ürün *</Label>
                            <button type="button" onClick={() => setScanLid(it._lid)} data-testid={`sample-item-scan-${idx}`}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-blue-300 hover:text-blue-200">
                              <ScanLine className="w-3 h-3" /> Barkod Tara
                            </button>
                          </div>
                          <SearchPicker
                            items={[]}
                            value={it.product_id}
                            onChange={(v) => patchRow(it._lid, { product_id: v, variant_id: "", variant_name: "" })}
                            placeholder="Ürün adı yazın..."
                            testId={`sample-item-product-${idx}`}
                            getLabel={(p) => `${p.name}${p.code ? ` (${p.code})` : ""}`}
                            getSubtitle={(p) => p.brand_name || ""}
                            onSearch={searchProducts}
                            selectedItem={productCache[it.product_id] || scanned[it.product_id]}
                          />
                          {it.variant_id && (
                            <div className="mt-1 text-[11px] font-mono text-emerald-300" data-testid={`sample-item-variant-${idx}`}>Beden: {it.variant_name || "—"}</div>
                          )}
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-slate-500">Kaynak Depo *</Label>
                          <Select value={it.source_warehouse_id} onValueChange={(v) => patchRow(it._lid, { source_warehouse_id: v })}>
                            <SelectTrigger className="h-8 rounded-sm bg-slate-950 border-slate-800 text-xs" data-testid={`sample-item-wh-${idx}`}>
                              <SelectValue placeholder="Depo seç..." />
                            </SelectTrigger>
                            <SelectContent>
                              {warehouses.map((w) => (
                                <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <Label className="text-[10px] uppercase text-slate-500">Marka</Label>
                          <Input value={it.brand_name} onChange={(e) => patchRow(it._lid, { brand_name: e.target.value })} className="h-8 rounded-sm bg-slate-950 border-slate-800 text-xs" placeholder="Marka..." />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-slate-500">Ürün Adı *</Label>
                          <Input value={it.product_name} onChange={(e) => patchRow(it._lid, { product_name: e.target.value })} className="h-8 rounded-sm bg-slate-950 border-slate-800 text-xs" placeholder="Ürün adı" data-testid={`sample-item-off-name-${idx}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-slate-500">Beden/Varyant</Label>
                          <Input value={it.variant_name} onChange={(e) => patchRow(it._lid, { variant_name: e.target.value })} className="h-8 rounded-sm bg-slate-950 border-slate-800 text-xs" placeholder="S / M / L..." />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase text-slate-500">Adet *</Label>
                      <Input type="number" min={1} value={it.given_qty} onChange={(e) => patchRow(it._lid, { given_qty: e.target.value })} className="h-8 rounded-sm bg-slate-950 border-slate-800 text-xs" data-testid={`sample-item-qty-${idx}`} />
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-[10px] uppercase text-slate-500">Satır Not</Label>
                      <Input value={it.note} onChange={(e) => patchRow(it._lid, { note: e.target.value })} className="h-8 rounded-sm bg-slate-950 border-slate-800 text-xs" placeholder="Bu satıra özel not..." />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-sm">İptal</Button>
          <Button onClick={submit} disabled={saving} className="bg-cyan-600 hover:bg-cyan-500 rounded-sm" data-testid="sample-create-submit">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Kaydet
        <BarcodeScanner open={!!scanLid} onOpenChange={(o) => !o && setScanLid("")} onDetected={onSampleScan} />
        <UnknownBarcodeDialog code={unknownScan?.code || ""} onClose={() => setUnknownScan(null)}
          onAssigned={(r) => { const lid = unknownScan.lid; setUnknownScan(null); applyScan(lid, r); }} />

          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Detail Dialog
// ============================================================================
function DetailDialog({ sample, onClose, onReturn, onComplimentary, onNavigateSale, canEdit }) {
  const kpi = sample.kpi || {};
  const hasAtCustomer = (kpi.at_customer || 0) > 0;
  return (
    <Dialog open={!!sample} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl bg-slate-900 border-slate-800" data-testid="sample-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Boxes className="w-5 h-5 text-cyan-400" />
            {sample.customer_name || "Numune"}
            <Badge className={`text-[10px] rounded-sm ${STATUS_TONE[sample.status]}`}>{STATUS_LABEL[sample.status] || sample.status}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <MiniStat label="Verilen" value={kpi.given} tone="slate" />
            <MiniStat label="İade" value={kpi.returned} tone="emerald" />
            <MiniStat label="Müşteride" value={kpi.at_customer} tone={hasAtCustomer ? "sky" : "slate"} />
            <MiniStat label="Hasarlı" value={kpi.damaged} tone="amber" />
            <MiniStat label="Kayıp / Bedelsiz" value={(kpi.lost || 0) + (kpi.complimentary || 0)} tone="rose" />
          </div>
          <div className="border border-slate-800 rounded-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-950/60">
                <tr className="text-slate-400 uppercase tracking-wider text-[10px]">
                  <th className="text-left px-3 py-2">Ürün</th>
                  <th className="text-left px-3 py-2">Kaynak</th>
                  <th className="text-center px-3 py-2">Verilen</th>
                  <th className="text-center px-3 py-2">İade</th>
                  <th className="text-center px-3 py-2">Müşteride</th>
                  <th className="text-center px-3 py-2">Hasarlı</th>
                  <th className="text-center px-3 py-2">Kayıp</th>
                  <th className="text-center px-3 py-2">Satış</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {(sample.items || []).map((it) => {
                  const atc = Math.max(0, (it.given_qty || 0) - (it.returned_qty || 0) - (it.damaged_qty || 0) - (it.lost_qty || 0) - (it.converted_to_sale_qty || 0) - (it.complimentary_qty || 0));
                  return (
                    <tr key={it.id} className="text-slate-300" data-testid={`detail-item-${it.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-slate-100">{normalizeItemLabel(it)}</div>
                        {it.brand_name && <div className="text-[10px] text-slate-500">{it.brand_name}</div>}
                      </td>
                      <td className="px-3 py-2 text-[11px]">
                        {it.source_type === "stock" ? (
                          <span className="text-emerald-400">Depo: {it.source_warehouse_name || "—"}</span>
                        ) : (
                          <span className="text-slate-400">Stok Dışı</span>
                        )}
                      </td>
                      <td className="text-center font-mono">{it.given_qty}</td>
                      <td className="text-center font-mono text-emerald-400">{it.returned_qty || 0}</td>
                      <td className="text-center font-mono">
                        <span className={atc > 0 ? "text-sky-400 font-bold" : "text-slate-500"}>{atc}</span>
                      </td>
                      <td className="text-center font-mono text-amber-400">{it.damaged_qty || 0}</td>
                      <td className="text-center font-mono text-rose-400">{(it.lost_qty || 0) + (it.complimentary_qty || 0)}</td>
                      <td className="text-center font-mono text-cyan-400">{it.converted_to_sale_qty || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Status history */}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Geçmiş</div>
            <div className="space-y-1.5">
              {(sample.status_history || []).slice().reverse().map((h, i) => (
                <div key={i} className="text-xs text-slate-400 flex gap-2">
                  <span className="text-slate-500 font-mono">{fmtDate(h.at)}</span>
                  <span className="text-slate-300">{STATUS_LABEL[h.status] || h.status}</span>
                  <span className="text-slate-500 truncate">— {h.note}</span>
                </div>
              ))}
            </div>
          </div>

          {/* P3: Numune Fotoğrafları (opsiyonel kanıt) */}
          <SamplePhotos sampleId={sample.id} canEdit={canEdit} />
        </div>
        <DialogFooter className="border-t border-slate-800 pt-3">
          <div className="flex-1 flex items-center gap-2 flex-wrap">
            {canEdit && hasAtCustomer && (
              <>
                <Button variant="outline" onClick={onReturn} className="rounded-sm text-xs border-emerald-700 text-emerald-300" data-testid="sample-detail-return-btn">
                  <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Numune İade Al
                </Button>
                <Button variant="outline" onClick={onNavigateSale} className="rounded-sm text-xs border-cyan-700 text-cyan-300" data-testid="sample-detail-tosale-btn">
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Satışa Dönüştür
                </Button>
                <Button variant="outline" onClick={onComplimentary} className="rounded-sm text-xs border-amber-700 text-amber-300" data-testid="sample-detail-comp-btn">
                  <Gift className="w-3.5 h-3.5 mr-1" /> Bedelsiz / Sarf
                </Button>
              </>
            )}
          </div>
          <Button variant="ghost" onClick={onClose} className="rounded-sm">Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ label, value, tone }) {
  const toneCls = {
    slate: "text-slate-300 border-slate-800",
    sky: "text-sky-300 border-sky-900/50 bg-sky-950/20",
    emerald: "text-emerald-300 border-emerald-900/50 bg-emerald-950/20",
    amber: "text-amber-300 border-amber-900/50 bg-amber-950/20",
    rose: "text-rose-300 border-rose-900/50 bg-rose-950/20",
  }[tone] || "text-slate-300 border-slate-800";
  return (
    <div className={`rounded-sm border px-3 py-2 ${toneCls}`}>
      <div className="text-2xl font-bold font-mono">{value || 0}</div>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
    </div>
  );
}

// ============================================================================
// Return Dialog (per-item, target warehouse)
// ============================================================================
function ReturnDialog({ sample, warehouses, searchProducts, productCache, onClose, onSaved }) {
  const [defaultTarget, setDefaultTarget] = useState("");
  const [lines, setLines] = useState(() =>
    (sample.items || []).map((it) => {
      const remaining = Math.max(0, (it.given_qty || 0) - (it.returned_qty || 0) - (it.damaged_qty || 0) - (it.lost_qty || 0) - (it.converted_to_sale_qty || 0) - (it.complimentary_qty || 0));
      return {
        item: it, remaining,
        returned_qty: 0, damaged_qty: 0, lost_qty: 0,
        target_warehouse_id: it.source_warehouse_id || "",
        off_stock_action: "close_off_stock",
        match_product_id: "",
      };
    })
  );
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const patch = (id, p) => setLines((s) => s.map((l) => l.item.id === id ? { ...l, ...p } : l));
  const applyDefaultTarget = (whId) => {
    setDefaultTarget(whId);
    setLines((s) => s.map((l) => ({ ...l, target_warehouse_id: whId })));
  };

  const submit = async () => {
    const active = lines.filter((l) => (l.returned_qty + l.damaged_qty + l.lost_qty) > 0);
    if (active.length === 0) { toast.error("En az bir satır için miktar girin"); return; }
    for (const l of active) {
      const total = Number(l.returned_qty) + Number(l.damaged_qty) + Number(l.lost_qty);
      if (total > l.remaining) { toast.error(`${normalizeItemLabel(l.item)}: toplam ${total} > müşteride kalan ${l.remaining}`); return; }
      if (l.returned_qty > 0 && l.item.source_type === "stock" && !l.target_warehouse_id) {
        toast.error(`${normalizeItemLabel(l.item)}: sağlam iade için hedef depo zorunlu`); return;
      }
      if (l.returned_qty > 0 && l.item.source_type === "off_stock" && l.off_stock_action === "add_to_stock") {
        if (!l.match_product_id || !l.target_warehouse_id) {
          toast.error(`${normalizeItemLabel(l.item)}: 'Depoya Al' için ürün eşleştirme + hedef depo zorunlu`); return;
        }
      }
    }
    setSaving(true);
    try {
      await api.post(`/samples/${sample.id}/return`, {
        note,
        lines: active.map((l) => ({
          item_id: l.item.id,
          returned_qty: Number(l.returned_qty) || 0,
          damaged_qty: Number(l.damaged_qty) || 0,
          lost_qty: Number(l.lost_qty) || 0,
          target_warehouse_id: l.target_warehouse_id || "",
          off_stock_action: l.item.source_type === "off_stock" ? l.off_stock_action : null,
          match_product_id: l.match_product_id || "",
        })),
      });
      toast.success("İade işlendi");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl bg-slate-900 border-slate-800" data-testid="sample-return-dialog">
        <DialogHeader>
          <DialogTitle>Numune İade Al — {sample.customer_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs">Tümüne Hedef Depo Uygula</Label>
              <Select value={defaultTarget} onValueChange={applyDefaultTarget}>
                <SelectTrigger className="rounded-sm bg-slate-950 border-slate-800" data-testid="return-default-wh">
                  <SelectValue placeholder="Bir depo seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            {lines.map((l) => (
              <div key={l.item.id} className={`border rounded-sm p-3 space-y-2 ${l.remaining > 0 ? "border-slate-700" : "border-slate-800 opacity-60"}`} data-testid={`return-line-${l.item.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-100">{normalizeItemLabel(l.item)}</div>
                    <div className="text-[11px] text-slate-500">
                      {l.item.source_type === "stock" ? `Depo: ${l.item.source_warehouse_name}` : "Stok Dışı"} · Kalan: <span className="font-bold text-sky-400">{l.remaining}</span>
                    </div>
                  </div>
                </div>
                {l.remaining > 0 && (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500">Sağlam İade</Label>
                        <Input type="number" min={0} max={l.remaining} value={l.returned_qty} onChange={(e) => patch(l.item.id, { returned_qty: Number(e.target.value) || 0 })} className="h-8 rounded-sm bg-slate-950 border-slate-800 text-xs" data-testid={`return-${l.item.id}-returned`} />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500">Hasarlı</Label>
                        <Input type="number" min={0} max={l.remaining} value={l.damaged_qty} onChange={(e) => patch(l.item.id, { damaged_qty: Number(e.target.value) || 0 })} className="h-8 rounded-sm bg-slate-950 border-slate-800 text-xs" data-testid={`return-${l.item.id}-damaged`} />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500">Kayıp</Label>
                        <Input type="number" min={0} max={l.remaining} value={l.lost_qty} onChange={(e) => patch(l.item.id, { lost_qty: Number(e.target.value) || 0 })} className="h-8 rounded-sm bg-slate-950 border-slate-800 text-xs" data-testid={`return-${l.item.id}-lost`} />
                      </div>
                    </div>
                    {l.returned_qty > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1 border-t border-slate-800">
                        {l.item.source_type === "stock" ? (
                          <div className="md:col-span-2">
                            <Label className="text-[10px] uppercase text-slate-500">Hedef Depo (Sağlam İade) *</Label>
                            <Select value={l.target_warehouse_id} onValueChange={(v) => patch(l.item.id, { target_warehouse_id: v })}>
                              <SelectTrigger className="h-8 rounded-sm bg-slate-950 border-slate-800 text-xs">
                                <SelectValue placeholder="Depo seç..." />
                              </SelectTrigger>
                              <SelectContent>
                                {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <>
                            <div>
                              <Label className="text-[10px] uppercase text-slate-500">Stok Dışı İade Aksiyonu</Label>
                              <Select value={l.off_stock_action} onValueChange={(v) => patch(l.item.id, { off_stock_action: v })}>
                                <SelectTrigger className="h-8 rounded-sm bg-slate-950 border-slate-800 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="close_off_stock">Stok Dışı Olarak Kapat</SelectItem>
                                  <SelectItem value="add_to_stock">Depoya Al (Ürün Eşleştir)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {l.off_stock_action === "add_to_stock" && (
                              <>
                                <div>
                                  <Label className="text-[10px] uppercase text-slate-500">Eşleştirilecek Ürün *</Label>
                                  <SearchPicker items={[]} value={l.match_product_id} onChange={(v) => patch(l.item.id, { match_product_id: v })} placeholder="Ürün adı yazın..." getLabel={(p) => p.name} getSubtitle={(p) => p.code || ""} onSearch={searchProducts} selectedItem={productCache[l.match_product_id]} testId={`return-match-${l.item.id}`} />
                                </div>
                                <div className="md:col-span-2">
                                  <Label className="text-[10px] uppercase text-slate-500">Hedef Depo *</Label>
                                  <Select value={l.target_warehouse_id} onValueChange={(v) => patch(l.item.id, { target_warehouse_id: v })}>
                                    <SelectTrigger className="h-8 rounded-sm bg-slate-950 border-slate-800 text-xs">
                                      <SelectValue placeholder="Depo seç..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          <div>
            <Label className="text-xs">Genel Not</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="rounded-sm bg-slate-950 border-slate-800" placeholder="İade notu..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-sm">İptal</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 rounded-sm" data-testid="sample-return-submit">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} İade İşle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Complimentary Dialog
// ============================================================================
function ComplimentaryDialog({ sample, onClose, onSaved }) {
  const [lines, setLines] = useState(() =>
    (sample.items || []).map((it) => {
      const remaining = Math.max(0, (it.given_qty || 0) - (it.returned_qty || 0) - (it.damaged_qty || 0) - (it.lost_qty || 0) - (it.converted_to_sale_qty || 0) - (it.complimentary_qty || 0));
      return { item: it, remaining, quantity: 0 };
    })
  );
  const [reason, setReason] = useState("Numune müşteride bırakıldı");
  const [saving, setSaving] = useState(false);
  const patch = (id, q) => setLines((s) => s.map((l) => l.item.id === id ? { ...l, quantity: q } : l));

  const submit = async () => {
    const active = lines.filter((l) => l.quantity > 0);
    if (!active.length) { toast.error("En az bir satır için miktar girin"); return; }
    for (const l of active) {
      if (l.quantity > l.remaining) { toast.error(`${normalizeItemLabel(l.item)}: ${l.quantity} > kalan ${l.remaining}`); return; }
    }
    if (!window.confirm(`${active.reduce((s, l) => s + l.quantity, 0)} adet BEDELSİZ/SARF olarak kapatılacak. Ana depoya stok girişi YOK. Onaylıyor musunuz?`)) return;
    setSaving(true);
    try {
      await api.post(`/samples/${sample.id}/complimentary`, {
        reason,
        lines: active.map((l) => ({ item_id: l.item.id, quantity: Number(l.quantity) })),
      });
      toast.success("Bedelsiz/Sarf işlendi");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-slate-900 border-slate-800" data-testid="sample-comp-dialog">
        <DialogHeader>
          <DialogTitle>Bedelsiz / Sarf Olarak Kapat</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-amber-300 bg-amber-950/30 border border-amber-900/50 rounded-sm p-2">
            Bu işlem stok değiştirmez. Müşteride kalan ürün geri gelmeyecek ve fatura edilmeyecekse kullanın.
          </div>
          <div className="space-y-2">
            {lines.map((l) => (
              <div key={l.item.id} className={`border rounded-sm p-3 ${l.remaining > 0 ? "border-slate-700" : "border-slate-800 opacity-60"}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-100">{normalizeItemLabel(l.item)}</div>
                    <div className="text-[11px] text-slate-500">Kalan: <span className="font-bold text-sky-400">{l.remaining}</span></div>
                  </div>
                  {l.remaining > 0 && (
                    <div className="w-24">
                      <Input type="number" min={0} max={l.remaining} value={l.quantity} onChange={(e) => patch(l.item.id, Number(e.target.value) || 0)} className="h-8 rounded-sm bg-slate-950 border-slate-800 text-xs text-right" data-testid={`comp-${l.item.id}-qty`} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div>
            <Label className="text-xs">Neden</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-sm bg-slate-950 border-slate-800" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-sm">İptal</Button>
          <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-500 rounded-sm" data-testid="sample-comp-submit">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Bedelsiz Kapat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Convert-to-Sale Dialog (P3 Numune → Sipariş Bağlantısı)
// ============================================================================
function ConvertToSaleDialog({ sample, onClose, onConfirm }) {
  const [lines, setLines] = useState(() =>
    (sample.items || []).map((it) => {
      const remaining = Math.max(0, (it.given_qty || 0) - (it.returned_qty || 0) - (it.damaged_qty || 0) - (it.lost_qty || 0) - (it.converted_to_sale_qty || 0) - (it.complimentary_qty || 0));
      return { item: it, remaining, quantity: 0, unit_price: it.unit_value || 0 };
    })
  );
  const patch = (id, p) => setLines((s) => s.map((l) => l.item.id === id ? { ...l, ...p } : l));

  const submit = () => {
    const active = lines.filter((l) => l.quantity > 0);
    if (!active.length) { toast.error("En az bir satır için miktar girin"); return; }
    for (const l of active) {
      if (l.quantity > l.remaining) { toast.error(`${normalizeItemLabel(l.item)}: ${l.quantity} > kalan ${l.remaining}`); return; }
    }
    // Build intent for OrderWizard
    const intent = {
      sample_id: sample.id,
      customer_id: sample.customer_id,
      customer_name: sample.customer_name,
      created_at: new Date().toISOString(),
      lines: active.map((l) => ({
        item_id: l.item.id,
        product_id: l.item.product_id || "",
        variant_id: l.item.variant_id || "",
        product_name: l.item.product_name || "",
        variant_name: l.item.variant_name || "",
        quantity: Number(l.quantity),
        unit_price: Number(l.unit_price) || 0,
      })),
    };
    onConfirm(intent);
  };

  const hasStockLess = lines.some((l) => l.item.source_type === "off_stock" && !l.item.product_id);
  const stockLessLines = lines.filter((l) => l.item.source_type === "off_stock" && !l.item.product_id);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl bg-slate-900 border-slate-800" data-testid="sample-convert-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-cyan-400" />
            Satışa Dönüştür — {sample.customer_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <div className="text-xs text-cyan-300 bg-cyan-950/30 border border-cyan-900/50 rounded-sm p-2">
            Sipariş Sihirbazı seçili müşteri ve satırlar ile açılacak. Numune tesliminde stok zaten düştüğü için sipariş kaydında <b>ikinci kez stok düşmez</b>. Sipariş başarıyla oluşturulduğunda numune otomatik "converted" işaretlenir.
          </div>
          {hasStockLess && (
            <div className="text-xs text-amber-300 bg-amber-950/30 border border-amber-900/50 rounded-sm p-2">
              Aşağıdaki satırlar Stok Dışı Numune ve ürün kartı ile eşleşmemiş — sipariş satırında ürün seçimi manuel yapılmalı:
              <ul className="list-disc list-inside mt-1">
                {stockLessLines.map((l) => <li key={l.item.id}>{normalizeItemLabel(l.item)}</li>)}
              </ul>
            </div>
          )}
          <div className="space-y-2">
            {lines.map((l) => (
              <div key={l.item.id} className={`border rounded-sm p-3 ${l.remaining > 0 ? "border-slate-700" : "border-slate-800 opacity-50"}`} data-testid={`convert-line-${l.item.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-sm font-semibold text-slate-100">{normalizeItemLabel(l.item)}</div>
                    <div className="text-[11px] text-slate-500">
                      Kalan müşteride: <span className="font-bold text-sky-400">{l.remaining}</span>
                      {l.item.source_type === "off_stock" && <span className="ml-1 text-amber-400">· Stok Dışı</span>}
                    </div>
                  </div>
                </div>
                {l.remaining > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase text-slate-500">Satışa Dönüşecek Adet *</Label>
                      <Input type="number" min={0} max={l.remaining} value={l.quantity} onChange={(e) => patch(l.item.id, { quantity: Number(e.target.value) || 0 })} className="h-8 rounded-sm bg-slate-950 border-slate-800 text-xs text-right" data-testid={`convert-${l.item.id}-qty`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-slate-500">Birim Fiyat (₺)</Label>
                      <Input type="number" min={0} step="0.01" value={l.unit_price} onChange={(e) => patch(l.item.id, { unit_price: Number(e.target.value) || 0 })} className="h-8 rounded-sm bg-slate-950 border-slate-800 text-xs text-right" data-testid={`convert-${l.item.id}-price`} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-sm">İptal</Button>
          <Button onClick={submit} className="bg-cyan-600 hover:bg-cyan-500 rounded-sm" data-testid="sample-convert-confirm">
            <ExternalLink className="w-4 h-4 mr-2" /> Sipariş Sihirbazına Git
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ============================================================================
// P3: Sample Photos (opsiyonel kanıt fotoğrafları)
// Backend: /api/samples/{id}/photos  (upload, list, download, delete)
// Constraints: max 5 foto/numune · 5 MB/foto · jpg/png/webp
// ============================================================================
const MAX_PHOTOS = 5;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const PHOTO_ALLOWED = ["image/jpeg", "image/png", "image/webp"];
const PHOTO_EXT_HINT = ".jpg,.jpeg,.png,.webp";

function SamplePhotos({ sampleId, canEdit }) {
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState({}); // { photoId: blobUrl }
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);

  const loadOne = useCallback(async (p) => {
    if (previews[p.id]) return;
    try {
      const r = await api.get(`/samples/${sampleId}/photos/${p.id}`, { responseType: "blob" });
      setPreviews((prev) => ({ ...prev, [p.id]: URL.createObjectURL(r.data) }));
    } catch { /* ignore per-photo error */ }
  }, [sampleId, previews]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/samples/${sampleId}/photos`);
      const arr = data || [];
      setPhotos(arr);
      arr.forEach(loadOne);
    } catch (e) {
      // sessiz: 403/404 durumunda boş kal
      setPhotos([]);
    } finally { setLoading(false); }
  }, [sampleId, loadOne]);

  useEffect(() => { reload(); return () => { Object.values(previews).forEach((u) => URL.revokeObjectURL(u)); }; }, [sampleId]); // eslint-disable-line

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) { toast.error(`En fazla ${MAX_PHOTOS} fotoğraf yükleyebilirsiniz`); return; }
    const files = Array.from(fileList).slice(0, remaining);
    setUploading(true);
    let uploaded = 0;
    for (const f of files) {
      if (!PHOTO_ALLOWED.includes(f.type)) { toast.error(`${f.name}: desteklenmeyen tür`); continue; }
      if (f.size > MAX_PHOTO_BYTES) { toast.error(`${f.name}: 5 MB'ı aşıyor`); continue; }
      try {
        const fd = new FormData(); fd.append("file", f);
        await api.post(`/samples/${sampleId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded += 1;
      } catch (e) { toast.error(`${f.name}: ${formatApiError(e)}`); }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    if (uploaded > 0) { toast.success(`${uploaded} fotoğraf yüklendi`); reload(); }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm("Bu fotoğrafı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/samples/${sampleId}/photos/${photoId}`);
      toast.success("Fotoğraf silindi");
      // free the local preview URL
      setPreviews((prev) => { const c = { ...prev }; if (c[photoId]) { URL.revokeObjectURL(c[photoId]); delete c[photoId]; } return c; });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="sample-photos-section">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
          <Camera className="w-3.5 h-3.5" /> Fotoğraflar ({photos.length}/{MAX_PHOTOS})
          <span className="text-[10px] text-slate-600 normal-case">— opsiyonel kanıt</span>
        </div>
        {canEdit && photos.length < MAX_PHOTOS && (
          <>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept={PHOTO_EXT_HINT}
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
              data-testid="sample-photo-file-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="rounded-sm text-xs border-slate-700"
              data-testid="sample-photo-upload-btn"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
              Fotoğraf Ekle
            </Button>
          </>
        )}
      </div>
      {loading ? (
        <div className="text-xs text-slate-500 py-2">Yükleniyor…</div>
      ) : photos.length === 0 ? (
        <div className="text-xs text-slate-500 py-2 border border-dashed border-slate-800 rounded-sm text-center">
          Henüz fotoğraf eklenmedi
        </div>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
          {photos.map((p) => (
            <div key={p.id} className="relative group border border-slate-800 rounded-sm overflow-hidden bg-slate-950 aspect-square" data-testid={`sample-photo-${p.id}`}>
              {previews[p.id] ? (
                <img
                  src={previews[p.id]}
                  alt={p.original_filename}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => setLightbox({ url: previews[p.id], name: p.original_filename })}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-slate-700" />
                </div>
              )}
              {canEdit && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }}
                  className="absolute top-1 right-1 bg-rose-600/90 hover:bg-rose-500 text-white p-1 rounded-sm opacity-0 group-hover:opacity-100 transition"
                  data-testid={`sample-photo-delete-${p.id}`}
                  aria-label="Fotoğrafı sil"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {lightbox && (
        <Dialog open onOpenChange={(v) => !v && setLightbox(null)}>
          <DialogContent className="max-w-4xl bg-slate-950 border-slate-800 p-2" data-testid="sample-photo-lightbox">
            <DialogHeader className="px-2 pt-1">
              <DialogTitle className="text-sm text-slate-300 truncate">{lightbox.name}</DialogTitle>
            </DialogHeader>
            <img src={lightbox.url} alt={lightbox.name} className="max-h-[75vh] w-full object-contain rounded-sm" />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
